// ===================================
// TRENCH QUEST - Market Updates Hook
// Real-time market simulation updates
// ===================================

import { useEffect, useRef, useCallback } from 'react'
import { useGameStore } from '@/store'
import type { GameSession } from '@/types'
import type { SimulatedMarketState, SimulatedMarketEvent } from '@/types/simulatedMarket'
import { updateSimulatedMarket } from '@/game/market/simulatedMarketEngine'

const UPDATE_INTERVAL_MS = 5000 // Update every 5 seconds

interface UseMarketUpdatesOptions {
  enabled?: boolean
  onMarketEvent?: (event: SimulatedMarketEvent) => void
  onPriceUpdate?: (prices: Record<string, number>) => void
}

/**
 * Hook for real-time market simulation updates
 * Updates prices every 5 seconds based on game time acceleration
 */
export function useMarketUpdates(
  session: GameSession | null,
  simulatedMarket: SimulatedMarketState | null,
  options: UseMarketUpdatesOptions = {}
) {
  const { enabled = true, onMarketEvent, onPriceUpdate } = options
  const { setSession } = useGameStore()

  const lastUpdateRef = useRef<number>(Date.now())
  const marketRef = useRef<SimulatedMarketState | null>(simulatedMarket)

  // Keep market ref in sync
  useEffect(() => {
    marketRef.current = simulatedMarket
  }, [simulatedMarket])

  // Update market prices
  const updateMarket = useCallback(() => {
    if (!session || !marketRef.current) return

    const now = Date.now()
    const elapsed = now - lastUpdateRef.current
    lastUpdateRef.current = now

    // Run market simulation
    const { market: updatedMarket, events, gameHoursElapsed } = updateSimulatedMarket(
      marketRef.current,
      elapsed,
      session.difficulty
    )

    if (gameHoursElapsed > 0) {
      // Update session with new market prices
      const updatedHoldings = session.player.portfolio.holdings.map(holding => {
        const newPrice = updatedMarket.prices[holding.symbol]?.currentPrice
        return {
          ...holding,
          currentPrice: newPrice ?? holding.currentPrice,
        }
      })

      const newTotalValue = updatedHoldings.reduce(
        (sum, h) => sum + h.amount * h.currentPrice,
        0
      )

      // Also update the game's market state prices
      const updatedMarketPrices = { ...session.market.prices }
      for (const [symbol, simPrice] of Object.entries(updatedMarket.prices)) {
        if (updatedMarketPrices[symbol]) {
          updatedMarketPrices[symbol] = {
            ...updatedMarketPrices[symbol],
            price: simPrice.currentPrice,
            change24h: simPrice.change24h,
          }
        }
      }

      const updatedSession: GameSession = {
        ...session,
        market: {
          ...session.market,
          prices: updatedMarketPrices,
          condition: updatedMarket.condition,
          fearGreedIndex: updatedMarket.fearGreedIndex,
          ruggedTokens: updatedMarket.ruggedTokens,
        },
        player: {
          ...session.player,
          portfolio: {
            ...session.player.portfolio,
            holdings: updatedHoldings,
            totalValue: newTotalValue,
          },
        },
      }

      setSession(updatedSession)

      // Notify callbacks
      if (onPriceUpdate) {
        const prices: Record<string, number> = {}
        for (const [symbol, token] of Object.entries(updatedMarket.prices)) {
          prices[symbol] = token.currentPrice
        }
        onPriceUpdate(prices)
      }

      // Notify about market events
      if (onMarketEvent && events.length > 0) {
        for (const event of events) {
          onMarketEvent(event)
        }
      }
    }

    // Update ref with new market state
    marketRef.current = updatedMarket
  }, [session, setSession, onMarketEvent, onPriceUpdate])

  // Set up interval
  useEffect(() => {
    if (!enabled || !session || !simulatedMarket) return

    lastUpdateRef.current = Date.now()

    const interval = setInterval(updateMarket, UPDATE_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [enabled, session?.id, simulatedMarket, updateMarket])

  return {
    updateMarket, // Manual trigger if needed
  }
}

/**
 * Simple hook to get current game time display
 */
export function useGameTimeDisplay(session: GameSession | null) {
  if (!session) return null

  const { gameTime } = session
  const hour = gameTime.gameHour
  const minute = gameTime.gameMinute.toString().padStart(2, '0')
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour

  return {
    formatted: `Day ${gameTime.gameDay} - ${displayHour}:${minute} ${ampm}`,
    day: gameTime.gameDay,
    hour: gameTime.gameHour,
    minute: gameTime.gameMinute,
    totalHours: gameTime.totalGameHours,
  }
}
